import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useGetSellerQuotationById } from "@/hooks/seller/useGetSellerQuotationById"
import { useReviseQuotation } from "@/hooks/seller/useReviseQuotation"
import { useGetProductVariants } from "@/hooks/seller/useGetProductVariants"
import { useCountryCurrencies } from "@/hooks/useCountryCurrencies"
import { DatePicker } from "@/components/ui/date-picker"
import { useNavigate } from "@tanstack/react-router"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import {
  getCommodityError,
  getTotalPiecesError,
  getWeightError,
  getVolumeWeightError,
  QUANTITY_UNITS,
  isIntegerUnit,
  getChargeDescriptionError,
  getChargeUnitPriceError,
  getChargeQuantityError,
  getChargeUnitError,
  getChargeDiscountError,
  getChargeTaxError,
  isChargeRowCompleteAndValid,
} from "@/components/dashboard/seller/pages/rfqs/RfqDetailsPage"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, RfqDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import {
  Loader2,
  AlertCircle,
  FileText,
  Calendar,
  Package,
  Layers,
  ChevronLeft,
  ChevronDown,
  Check,
  Plus,
  Trash2,
  Edit,
  X,
  Sparkles,
  SlidersHorizontal,
  Tag,
  Hash,
  MapPin,
  Building2,
  MoreVertical,
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function getTodayDateString() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function QuotationDetailsPage({ quotationId }: { quotationId: string }) {
  const navigate = useNavigate()
  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/quotations" })
    }
  }

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetSellerQuotationById(quotationId)

  const quotationData = detailData?.sellerToAdminQuotation || null

  const handleRevised = (newId: string) => {
    navigate({
      to: "/quotations/$quotationId",
      params: { quotationId: newId },
    })
  }

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-primary/20 rounded-2xl shadow-sm w-full max-w-5xl mx-auto font-sans">
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary transition-all cursor-pointer text-slate-800"
          title="Back to Quotations"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-slate-500">Fetching quotation details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary transition-all cursor-pointer text-slate-800"
            title="Back to Quotations"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || "Failed to load quotation details."}</p>
        </div>
      </div>
    )
  }

  if (!quotationData) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary transition-all cursor-pointer text-slate-800"
            title="Back to Quotations"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">Quotation details not found.</p>
        </div>
      </div>
    )
  }

  return (
    <QuotationDetailView
      quotation={quotationData}
      onBack={handleGoBack}
      onRevised={handleRevised}
    />
  )
}

function QuotationDetailView({
  quotation,
  onBack,
  onRevised,
}: {
  quotation: any
  onBack: () => void
  onRevised: (newId: string) => void
}) {
  const { data: variants, isLoading: loadingVariants } = useGetProductVariants()
  const [isRevising, setIsRevising] = useState(false)
  const [showActionsDropdown, setShowActionsDropdown] = useState(false)
  const [selectedDetailVariant, setSelectedDetailVariant] = useState<any | null>(null)
  const [selectedDetailVariantId, setSelectedDetailVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedRfq, setSelectedRfq] = useState<any | null>(null)

  const [revisedMsg, setRevisedMsg] = useState("")
  const [revisedCurrency, setRevisedCurrency] = useState("")
  const [revisedValidUntil, setRevisedValidUntil] = useState("")
  const [isRevisedCurrencyDropdownOpen, setIsRevisedCurrencyDropdownOpen] = useState(false)
  const [currencySearch, setCurrencySearch] = useState("")
  const { data: countryCurrenciesData, isLoading: isLoadingCurrencies } = useCountryCurrencies()

  const currencyOptions = (countryCurrenciesData && countryCurrenciesData.length > 0)
    ? countryCurrenciesData.map((item) => ({
        id: item.id,
        code: item.code,
        country: item.country,
        name: `${item.code} - ${item.country}`,
      }))
    : [
        { id: 105, code: "INR", country: "INDIA", name: "INR - INDIA" },
        { id: 2, code: "USD", country: "UNITED STATES", name: "USD - UNITED STATES" },
        { id: 3, code: "EUR", country: "EUROPEAN UNION", name: "EUR - EUROPEAN UNION" },
        { id: 4, code: "GBP", country: "UNITED KINGDOM", name: "GBP - UNITED KINGDOM" },
        { id: 5, code: "AED", country: "UNITED ARAB EMIRATES", name: "AED - UNITED ARAB EMIRATES" },
        { id: 6, code: "SGD", country: "SINGAPORE", name: "SGD - SINGAPORE" },
      ]

  const filteredCurrencies = currencyOptions.filter((curr) => {
    if (!currencySearch.trim()) return true
    const q = currencySearch.toLowerCase().trim()
    return curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q)
  })
  const [revisedOriginCityId, setRevisedOriginCityId] = useState("")
  const [revisedCommodity, setRevisedCommodity] = useState("")
  const [revisedWeight, setRevisedWeight] = useState("")
  const [revisedVolumeWeight, setRevisedVolumeWeight] = useState("")
  const [revisedTotalPieces, setRevisedTotalPieces] = useState("")

  const commodityError = getCommodityError(revisedCommodity)
  const totalPiecesError = getTotalPiecesError(revisedTotalPieces)
  const weightError = getWeightError(revisedWeight)
  const volumeWeightError = getVolumeWeightError(revisedVolumeWeight)

  const revisedCurrencyDropdownRef = useRef<HTMLDivElement>(null)

  const [openProductDropdownIndex, setOpenProductDropdownIndex] = useState<number | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const variantDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openTaxTypeDropdownIndex, setOpenTaxTypeDropdownIndex] = useState<number | null>(null)
  const [taxTypeDropdownPosition, setTaxTypeDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const taxTypeDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openUnitDropdownIndex, setOpenUnitDropdownIndex] = useState<number | null>(null)
  const [unitDropdownPosition, setUnitDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const unitDropdownMenuRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (revisedCurrencyDropdownRef.current && !revisedCurrencyDropdownRef.current.contains(e.target as Node)) {
        setIsRevisedCurrencyDropdownOpen(false)
        setCurrencySearch("")
      }
      if (openProductDropdownIndex !== null) {
        const isMenu = variantDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="variant-${openProductDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenProductDropdownIndex(null)
        }
      }
      if (openTaxTypeDropdownIndex !== null) {
        const isMenu = taxTypeDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="taxtype-${openTaxTypeDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenTaxTypeDropdownIndex(null)
        }
      }
      if (openUnitDropdownIndex !== null) {
        const isMenu = unitDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="unit-${openUnitDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenUnitDropdownIndex(null)
        }
      }
    }
    const handleScroll = (e: Event) => {
      if (
        variantDropdownMenuRef.current?.contains(e.target as Node) ||
        taxTypeDropdownMenuRef.current?.contains(e.target as Node) ||
        unitDropdownMenuRef.current?.contains(e.target as Node)
      ) {
        return
      }
      setOpenProductDropdownIndex(null)
      setOpenTaxTypeDropdownIndex(null)
      setOpenUnitDropdownIndex(null)
    }

    document.addEventListener("mousedown", handleOutsideClick)
    window.addEventListener("scroll", handleScroll, true)
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [openProductDropdownIndex, openTaxTypeDropdownIndex, openUnitDropdownIndex])

  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!formError) return
    const timer = setTimeout(() => {
      setFormError(null)
    }, 5000)
    return () => clearTimeout(timer)
  }, [formError])

  type RevisedChargeItem = {
    description: string
    unitPrice: number | string | ""
    quantity: number | string | ""
    unit?: string
    discount: number | string | ""
    tax: number | string | ""
    taxType: string
    productVariantId?: string
    productVariant?: any
  }

  const DEFAULT_REVISED_CHARGE: RevisedChargeItem = {
    description: "",
    unitPrice: "",
    quantity: "",
    unit: "",
    discount: "",
    tax: "",
    taxType: "GST",
    productVariantId: "",
    productVariant: null,
  }

  const [revisedCharges, setRevisedCharges] = useState<RevisedChargeItem[]>([])

  const canAddCharge = revisedCharges.every(isChargeRowCompleteAndValid)

  const handleAddCharge = () => {
    if (revisedCharges.length > 1) {
      const isPreviousRowsValid = revisedCharges.slice(0, revisedCharges.length - 1).every(isChargeRowCompleteAndValid)
      const isLatestRowValid = isChargeRowCompleteAndValid(revisedCharges[revisedCharges.length - 1])

      if (!isPreviousRowsValid) {
        toast.error("Please complete the validation of the current charge before adding another.")
        return
      }
      if (!isLatestRowValid) {
        toast.error("Please first finish the latest added row before adding another.")
        return
      }
    } else if (!canAddCharge) {
      toast.error("Please complete the validation of the current charge before adding another.")
      return
    }
    setRevisedCharges([...revisedCharges, { ...DEFAULT_REVISED_CHARGE }])
  }

  const handleRemoveCharge = (index: number) => {
    setRevisedCharges(revisedCharges.filter((_, i) => i !== index))
  }

  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>, currentVal?: any, isIntegerOnly?: boolean) => {
    if (
      e.key === "-" ||
      e.key === "Subtract" ||
      e.code === "Minus" ||
      e.code === "NumpadSubtract" ||
      e.key === "+" ||
      e.key === "e" ||
      e.key === "E"
    ) {
      e.preventDefault()
    }
    if (isIntegerOnly && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
    if (e.key === "." && currentVal !== undefined && String(currentVal).includes(".")) {
      e.preventDefault()
    }
  }

  const handleChargeChange = (index: number, field: keyof RevisedChargeItem, value: any) => {
    let sanitized = value
    const currentUnit = field === "unit" ? value : revisedCharges[index]?.unit

    if (typeof sanitized === "string") {
      if (field === "quantity") {
        if (isIntegerUnit(currentUnit)) {
          sanitized = sanitized.split(".")[0].replace(/\D/g, "")
        } else {
          sanitized = sanitized.replace(/[^0-9.]/g, "")
          const parts = sanitized.split(".")
          if (parts.length > 2) {
            sanitized = parts[0] + "." + parts.slice(1).join("")
          }
        }
      } else if (["unitPrice", "discount", "tax"].includes(field as string)) {
        sanitized = sanitized.replace(/[^0-9.]/g, "")
        const parts = sanitized.split(".")
        if (parts.length > 2) {
          sanitized = parts[0] + "." + parts.slice(1).join("")
        }
      }
    }

    const updated = [...revisedCharges]
    const updatedCharge = { ...updated[index], [field]: sanitized }

    // If unit changed to Piece or Box, trim out decimal values from existing quantity
    if (field === "unit" && isIntegerUnit(sanitized)) {
      const currentQty = String(updatedCharge.quantity ?? "")
      if (currentQty.includes(".")) {
        updatedCharge.quantity = currentQty.split(".")[0].replace(/\D/g, "")
      }
    }

    updated[index] = updatedCharge
    setRevisedCharges(updated)
  }

  const reviseMutation = useReviseQuotation()

  const startRevision = () => {
    setFormError(null)
    setRevisedMsg(quotation.message || "")
    setRevisedCurrency(quotation.currencyCode || "INR")
    const todayStr = getTodayDateString()
    const rawValidUntil = quotation.expiresAt || quotation.validUntil || ""
    const datePart = rawValidUntil ? rawValidUntil.split("T")[0] : ""
    const initialValidUntil = datePart && datePart < todayStr ? todayStr : datePart

    setRevisedValidUntil(initialValidUntil)
    setRevisedOriginCityId(quotation.originCityId || "")
    setRevisedCommodity(quotation.commodity || "")
    setRevisedWeight(quotation.weight !== undefined && quotation.weight !== null ? String(quotation.weight) : "")
    setRevisedVolumeWeight(quotation.volumeWeight !== undefined && quotation.volumeWeight !== null ? String(quotation.volumeWeight) : "")
    setRevisedTotalPieces(quotation.totalPieces !== undefined && quotation.totalPieces !== null ? String(quotation.totalPieces) : "")
    const existingCharges = quotation.sellerGeneratedQuotationCharges || quotation.charges || []
    setRevisedCharges(
      existingCharges.length > 0
        ? existingCharges.map((c: any) => ({
            description: c.description || "",
            unitPrice: c.unitPrice !== undefined ? c.unitPrice : (c.amount !== undefined ? c.amount : ""),
            quantity: c.quantity !== undefined && c.quantity !== null ? c.quantity : "",
            unit: c.measureUnit || c.unit || "",
            discount: c.discount !== undefined && c.discount !== 0 ? c.discount : "",
            tax: c.tax !== undefined && c.tax !== 0 ? c.tax : "",
            taxType: c.taxType || "GST",
            productVariantId: c.productVariantId || (c.productVariant?.id || ""),
            productVariant: c.productVariant || null,
          }))
        : [{ ...DEFAULT_REVISED_CHARGE }]
    )
    setIsRevising(true)
  }

  const handleReviseSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const reportValidationError = (errMsg: string, scrollTargetId?: string) => {
      setFormError(errMsg)
      setTimeout(() => {
        if (scrollTargetId) {
          document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: "smooth", block: "center" })
        } else {
          document.getElementById("revision-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 50)
    }

    if (!revisedMsg.trim()) {
      reportValidationError("Quotation message is required.")
      return
    }

    if (!revisedCurrency.trim()) {
      reportValidationError("Currency code is required.")
      return
    }

    if (revisedCharges.length === 0) {
      reportValidationError("At least one charge is required.")
      return
    }

    for (let i = 0; i < revisedCharges.length; i++) {
      const charge = revisedCharges[i]
      if (!charge.description.trim()) {
        reportValidationError(`Description for charge #${i + 1} is required.`)
        return
      }
      if (charge.description.trim().length > 50) {
        reportValidationError(`Description for charge #${i + 1} cannot exceed 50 characters.`)
        return
      }
      if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice)) || Number(charge.unitPrice) <= 0) {
        reportValidationError(`Please enter a valid unit price greater than 0 for charge #${i + 1}.`)
        return
      }
      if (Number(charge.unitPrice) > 10_000_000) {
        reportValidationError(`Unit price for charge #${i + 1} cannot exceed 10,000,000.`)
        return
      }
      const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined && charge.quantity !== null
      if (hasQuantity) {
        if (isNaN(Number(charge.quantity)) || Number(charge.quantity) <= 0) {
          reportValidationError(`Please enter a valid quantity greater than 0 for charge #${i + 1}.`)
          return
        }
        if (isIntegerUnit(charge.unit) && String(charge.quantity).includes(".")) {
          reportValidationError(`Quantity for charge #${i + 1} must be a whole number for ${charge.unit}.`)
          return
        }
        if (String(charge.quantity).includes(".") && String(charge.quantity).split(".")[1]?.length > 2) {
          reportValidationError(`Quantity for charge #${i + 1} can have at most 2 decimal places.`)
          return
        }
        if (Number(charge.quantity) >= 100_000) {
          reportValidationError(`Quantity for charge #${i + 1} must be less than 100,000.`)
          return
        }
        if (!charge.unit || !charge.unit.trim()) {
          reportValidationError(`Please select a unit for charge #${i + 1}.`)
          return
        }
      }
      if (charge.discount !== "" && charge.discount !== 0 && (isNaN(Number(charge.discount)) || Number(charge.discount) < 0 || Number(charge.discount) >= 100)) {
        reportValidationError(`Discount for charge #${i + 1} must be between 0% and 99.99%.`)
        return
      }
      if (charge.tax !== "" && charge.tax !== 0 && (isNaN(Number(charge.tax)) || Number(charge.tax) < 0 || Number(charge.tax) >= 100)) {
        reportValidationError(`Tax for charge #${i + 1} must be between 0% and 99.99%.`)
        return
      }
    }

    if (commodityError || totalPiecesError || weightError || volumeWeightError) {
      const logErr = commodityError || totalPiecesError || weightError || volumeWeightError || "Invalid logistics details"
      const targetId = commodityError
        ? "revise-commodity"
        : weightError
        ? "revise-weight"
        : volumeWeightError
        ? "revise-volume-weight"
        : "revise-total-pieces"
      reportValidationError(logErr, targetId)
      return
    }

    const todayStr = getTodayDateString()
    const effectiveValidUntil = revisedValidUntil && revisedValidUntil < todayStr ? todayStr : revisedValidUntil

    try {
      const res = await reviseMutation.mutateAsync({
        id: quotation.id,
        payload: {
          message: revisedMsg.trim(),
          currencyCode: revisedCurrency,
          expiresAt: effectiveValidUntil ? (effectiveValidUntil.includes("T") ? effectiveValidUntil : `${effectiveValidUntil}T12:00:00.000Z`) : undefined,
          originCityId: revisedOriginCityId || undefined,
          commodity: revisedCommodity.trim() || undefined,
          weight: revisedWeight.trim() !== "" ? Number(revisedWeight) : undefined,
          volumeWeight: revisedVolumeWeight.trim() !== "" ? Number(revisedVolumeWeight) : undefined,
          totalPieces: revisedTotalPieces.trim() !== "" ? Number(revisedTotalPieces) : undefined,
          charges: revisedCharges.map((c) => ({
            description: c.description.trim(),
            unitPrice: Number(c.unitPrice),
            quantity: c.quantity !== "" && c.quantity !== undefined && c.quantity !== null ? Number(c.quantity) : null,
            measureUnit: c.unit?.trim() || undefined,
            discount: c.discount !== "" ? Number(c.discount) : 0,
            tax: c.tax !== "" ? Number(c.tax) : 0,
            taxType: c.taxType,
            productVariantId: c.productVariantId || undefined,
          })),
        },
      })
      const newQuotation = res?.sellerToAdminQuotation
      if (newQuotation && newQuotation.id) {
        onRevised(newQuotation.id)
      }
      toast.success("Quotation revised successfully")
      setIsRevising(false)
    } catch (err: any) {
      console.error(err)
      toast.error(err?.message || "Failed to revise quotation")
    }
  }

  const charges = quotation.sellerGeneratedQuotationCharges || quotation.adminToBuyerQuotationCharges || quotation.charges || []
  const totalAmount = charges.reduce((sum: number, c: any) => {
    if (c.unitPrice !== undefined) {
      const qty = (c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1
      const base = Number(c.unitPrice) * qty
      const subtotal = base - (base * (Number(c.discount || 0) / 100))
      return sum + Math.max(0, subtotal + (subtotal * (Number(c.tax || 0) / 100)))
    }
    return sum + (c.amount || 0)
  }, 0)

  return (
    <div className={cn("w-full animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto", isRevising && "pb-64 sm:pb-48 md:pb-16")}>
      <div className="group relative overflow-hidden bg-white border border-primary/20 hover:border-primary/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-xs transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-primary to-primary/80 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer p-1"
            >
              <ChevronLeft className="size-4" />
            </button>              
            <span className="text-sm text-slate-700 font-medium hidden sm:inline">Back to Quotations</span>
            <span className="text-sm text-slate-700 font-medium sm:hidden">Back to Quotes</span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
              quotation.status === "APPROVED"
                ? "bg-sky-50 text-sky-700 border-sky-200"
                : quotation.status === "PENDING"
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : "bg-red-50 text-red-700 border-red-100"
            }`}>
              {quotation.status === "APPROVED" && <Check className="size-3" />}
              {quotation.status}
            </span>
            
            {!isRevising && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  className="p-1.5 text-slate-500 hover:text-primary hover:bg-primary/5 hover:border-primary/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center"
                  title="Actions"
                >
                  <MoreVertical className="size-4" />
                </button>
                {showActionsDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-30" 
                      onClick={() => setShowActionsDropdown(false)} 
                    />
                    <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false)
                          startRevision()
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit className="size-3.5 text-slate-500" />
                        Revise Quotation
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="h-px bg-primary/10 w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">

                        {(() => {
              const rfqData = quotation.adminToSellerRfq || null
              if (!rfqData) return null

              const targets = (rfqData.targets || []).filter(
                (t: any) => t.product || t.productVariant || t.productId || t.productVariantId
              )

              return (
                <div className="space-y-4">
                  {targets.length > 0 && (
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Sparkles className="size-3.5 text-primary" /> Sourcing Target Item(s)
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {targets.map((target: any) => (
                          <TargetItemView 
                            key={target.id} 
                            target={target} 
                            onCompanyClick={(c) => setSelectedCompany(c)}
                            onProductClick={(p) => setSelectedProduct(p)}
                            onVariantClick={(v) => setSelectedDetailVariant(v)}
                            onExternalSellerClick={setSelectedExternalSeller}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })()}
            
            {/* Buyer Name */}
            {(quotation.buyer?.user?.name || quotation.rfq?.buyer?.user?.name || quotation.rfq?.buyerToAdminRfq?.buyer?.user?.name || quotation.adminToSellerRfq?.buyer?.user?.name) && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Building2 className="size-3.5 text-primary" /> Buyer Name
                </h4>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/15 bg-slate-50/20 p-3 shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-slate-700 font-semibold tracking-tight font-sans">
                      {quotation.buyer?.user?.name || quotation.rfq?.buyer?.user?.name || quotation.rfq?.buyerToAdminRfq?.buyer?.user?.name || quotation.adminToSellerRfq?.buyer?.user?.name}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Linked RFQ */}
            {(() => {
              const rfqData = quotation.rfq || quotation.adminToSellerRfq || quotation.buyerToAdminRfq || null
              const rfqMessage = rfqData?.queryMessage || (rfqData?.id ? `#${rfqData.id.slice(0, 8).toUpperCase()}` : "View RFQ Details")
              const rfqStatus = rfqData?.status || "UNKNOWN"
              if (!quotation.rfqId) return null;
              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <FileText className="size-3.5 text-primary" /> Linked RFQ
                  </h4>
                  <div 
                    onClick={() => {
                      if (rfqData) setSelectedRfq(rfqData)
                    }}
                    className="group/item flex items-center justify-between gap-4 rounded-xl border border-primary/15 hover:border-primary/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-extrabold text-primary hover:underline text-xs tracking-tight font-sans">
                        {rfqMessage}
                      </span>
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                      rfqStatus === "ACTIVE" || rfqStatus === "ACCEPTED" || rfqStatus === "OPEN"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : rfqStatus === "PENDING"
                        ? "bg-amber-50 text-amber-700 border-amber-100"
                        : rfqStatus === "CLOSED" || rfqStatus === "REJECTED"
                        ? "bg-red-50 text-red-700 border-red-100"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }`}>
                      {rfqStatus}
                    </span>
                  </div>
                </div>
              )
            })()}

            {isRevising ? (
              <form onSubmit={handleReviseSubmit} className="space-y-4 border border-primary/15 p-5 rounded-2xl bg-primary/[0.02] shadow-sm">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-black text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                    <Edit className="size-4 text-primary" /> Revise Quotation Form
                  </h4>
                  {reviseMutation.isPending && <Loader2 className="size-4 animate-spin text-primary" />}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Quotation Message</label>
                    <textarea
                      value={revisedMsg}
                      onChange={(e) => setRevisedMsg(e.target.value)}
                      placeholder="Enter revised terms or message..."
                      className="w-full bg-white border border-slate-200 p-3 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary h-20"
                      required
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-nowrap gap-4 sm:gap-8 md:col-span-2">
                    <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">Currency Code</label>
                      <div className="relative w-full sm:w-[80px]" ref={revisedCurrencyDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsRevisedCurrencyDropdownOpen(!isRevisedCurrencyDropdownOpen)}
                          disabled={reviseMutation.isPending}
                          className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-all duration-200 cursor-pointer text-left"
                        >
                          <span>{revisedCurrency}</span>
                          <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isRevisedCurrencyDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        
                        {isRevisedCurrencyDropdownOpen && (
                          <div 
                            className="absolute left-0 z-50 mt-1 w-56 sm:w-64 bg-white border border-slate-200 rounded-lg shadow-lg text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col"
                          >
                            {/* Search Header - solid filled background */}
                            <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                              <input
                                type="text"
                                value={currencySearch}
                                onChange={(e) => setCurrencySearch(e.target.value)}
                                placeholder="Search currency or country..."
                                className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-primary focus:ring-1 focus:ring-primary text-slate-700 placeholder:text-slate-400"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            </div>

                            {/* Scrollable list strictly below the header */}
                            <div className="max-h-[160px] overflow-y-auto py-1">
                              {isLoadingCurrencies && (
                                <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                                  <Loader2 className="size-3 animate-spin text-primary" />
                                  <span>Loading currencies...</span>
                                </div>
                              )}
                              {filteredCurrencies.map((curr, idx) => (
                                <button
                                  key={(curr as any).id ?? `${curr.code}-${idx}`}
                                  type="button"
                                  onClick={() => {
                                    setRevisedCurrency(curr.code)
                                    setIsRevisedCurrencyDropdownOpen(false)
                                    setCurrencySearch("")
                                  }}
                                  title={curr.name}
                                  className={cn(
                                    "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate block",
                                    revisedCurrency === curr.code ? "text-primary bg-primary/5 font-bold" : ""
                                  )}
                                >
                                  {curr.name}
                                </button>
                              ))}
                              {!isLoadingCurrencies && filteredCurrencies.length === 0 && (
                                <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                                  No currency found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">Valid Until</label>
                      <div className="w-full sm:w-[170px]">
                        <DatePicker
                          value={revisedValidUntil}
                          onChange={setRevisedValidUntil}
                          min={getTodayDateString()}
                          placeholder="Select valid date"
                          disabled={reviseMutation.isPending}
                          className="!h-[34px] !py-1.5 !text-xs !bg-white !rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-1" id="revise-commodity">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Commodity</label>
                    <input
                      type="text"
                      value={revisedCommodity}
                      onChange={(e) => setRevisedCommodity(e.target.value)}
                      placeholder="e.g. Handicrafts"
                      className={cn(
                        "w-full bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none transition-all duration-200",
                        commodityError
                          ? "border-primary focus:border-primary ring-1 ring-primary/20"
                          : "border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary"
                      )}
                    />
                    {commodityError && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {commodityError}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-1" id="revise-weight">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Weight (kg)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={revisedWeight}
                      onChange={(e) => {
                        const val = e.target.value
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          setRevisedWeight(val)
                        }
                      }}
                      onKeyDown={blockNegativeKeys}
                      placeholder="e.g. 100"
                      className={cn(
                        "w-full bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none transition-all duration-200",
                        weightError
                          ? "border-primary focus:border-primary ring-1 ring-primary/20"
                          : "border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary"
                      )}
                    />
                    {weightError && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {weightError}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-1" id="revise-volume-weight">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Volumetric Weight</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={revisedVolumeWeight}
                      onChange={(e) => {
                        const val = e.target.value
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          setRevisedVolumeWeight(val)
                        }
                      }}
                      onKeyDown={blockNegativeKeys}
                      placeholder="e.g. 130"
                      className={cn(
                        "w-full bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none transition-all duration-200",
                        volumeWeightError
                          ? "border-primary focus:border-primary ring-1 ring-primary/20"
                          : "border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary"
                      )}
                    />
                    {volumeWeightError && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {volumeWeightError}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1" id="revise-total-pieces">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Pieces</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={revisedTotalPieces}
                      onChange={(e) => {
                        const clean = e.target.value.replace(/\D/g, "")
                        setRevisedTotalPieces(clean)
                      }}
                      onKeyDown={(e) => {
                        blockNegativeKeys(e)
                        if (e.key === ".") e.preventDefault()
                      }}
                      placeholder="e.g. 21"
                      className={cn(
                        "w-full bg-white border px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none transition-all duration-200",
                        totalPiecesError
                          ? "border-primary focus:border-primary ring-1 ring-primary/20"
                          : "border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary"
                      )}
                    />
                    {totalPiecesError && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {totalPiecesError}
                      </p>
                    )}
                  </div>
                </div>

                <div id="revision-charges-section" className="space-y-2 pt-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Charges Breakdown</label>
                    <button
                      type="button"
                      onClick={handleAddCharge}
                      disabled={reviseMutation.isPending}
                      className="text-primary hover:text-primary/80 font-bold text-xs flex items-center gap-0.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="size-3" /> Add Charge Row
                    </button>
                  </div>

                  {formError && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 p-2.5 rounded-lg border border-primary/20 animate-in fade-in duration-200">
                      <AlertCircle className="size-4 shrink-0" />
                      <span>{formError}</span>
                    </div>
                  )}
                  
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm mt-2">
                    <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="p-3 w-56">Product (Optional)</th>
                          <th className="p-3">Charge Description</th>
                          <th className="p-3 text-center w-32">Unit Price</th>
                          <th className="p-3 text-center w-24">Quantity</th>
                          <th className="p-3 text-center w-28">Unit</th>
                          <th className="p-3 text-center w-28">Discount (%)</th>
                          <th className="p-3 text-center w-24">Tax (%)</th>
                          <th className="p-3 text-center w-24">Tax Type</th>
                          <th className="p-3 text-right">Total</th>
                          <th className="p-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {revisedCharges.length === 0 ? (
                          <tr><td colSpan={10} className="p-6 text-center text-slate-400 italic text-xs">No charges added yet.</td></tr>
                        ) : (
                          revisedCharges.map((charge, idx) => {
                            const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                            const base = (Number(charge.unitPrice) || 0) * qty
                            const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                            const total = subtotal + (subtotal * ((Number(charge.tax) || 0) / 100))

                            const descError = getChargeDescriptionError(charge.description)
                            const unitPriceError = getChargeUnitPriceError(charge.unitPrice)
                            const quantityError = getChargeQuantityError(charge.quantity, charge.unit)
                            const unitError = getChargeUnitError(charge.unit, charge.quantity)
                            const discountError = getChargeDiscountError(charge.discount)
                            const taxError = getChargeTaxError(charge.tax)

                            const isRowDisabled = idx > 0 && !revisedCharges.slice(0, idx).every(isChargeRowCompleteAndValid)

                            return (
                              <tr key={idx} className={cn("hover:bg-slate-50/50 transition-colors align-top", isRowDisabled && "bg-slate-50/40 opacity-70")}>
                                <td className="p-2 min-w-[200px]">
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      data-dropdown-trigger={`variant-${idx}`}
                                      onClick={(e) => {
                                        if (isRowDisabled) return
                                        const rect = e.currentTarget.getBoundingClientRect()
                                        setDropdownPosition({
                                          top: rect.bottom,
                                          left: rect.left,
                                          width: rect.width
                                        })
                                        setOpenProductDropdownIndex(prev => prev === idx ? null : idx)
                                      }}
                                      className={cn(
                                        "flex-1 min-w-0 flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                        isRowDisabled ? "border-slate-200 opacity-50 cursor-not-allowed bg-slate-50" : "border-slate-200 focus:border-primary"
                                      )}
                                      disabled={reviseMutation.isPending || loadingVariants || isRowDisabled}
                                    >
                                      <span className="truncate">
                                        {charge.productVariantId && Array.isArray(variants)
                                          ? variants.find((v: any) => v.id === charge.productVariantId)?.name || charge.productVariant?.name || "Select Variant"
                                          : charge.productVariant?.name || "Select Variant"
                                        }
                                      </span>
                                      <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openProductDropdownIndex === idx && "rotate-180")} />
                                    </button>

                                    {(charge.productVariantId || charge.productVariant) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const vId = charge.productVariantId || charge.productVariant?.id
                                          if (vId) {
                                            setSelectedDetailVariantId(vId)
                                          } else if (charge.productVariant) {
                                            setSelectedDetailVariant(charge.productVariant)
                                          }
                                        }}
                                        className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-primary/5 hover:text-primary hover:border-primary/20 text-slate-500 cursor-pointer transition-all duration-200 shrink-0 h-[34px] w-[34px] flex items-center justify-center"
                                        title="View Variant Details"
                                      >
                                        <Layers className="size-3.5" />
                                      </button>
                                    )}

                                    {openProductDropdownIndex === idx && dropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                      <ul 
                                        ref={variantDropdownMenuRef}
                                        style={{
                                          position: 'fixed',
                                          top: `${dropdownPosition.top + 4}px`,
                                          left: `${dropdownPosition.left}px`,
                                          width: `${dropdownPosition.width}px`,
                                        }}
                                        className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                      >
                                        <li
                                          onClick={() => {
                                            const next = [...revisedCharges];
                                            const targetCharge = { 
                                              ...next[idx], 
                                              productVariantId: "", 
                                              productVariant: null, 
                                              unitPrice: "" as const, 
                                              description: "" 
                                            };
                                            next[idx] = targetCharge;
                                            setRevisedCharges(next);
                                            setOpenProductDropdownIndex(null);
                                          }}
                                          className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 cursor-pointer transition-colors border-b border-slate-50 last:border-0"
                                        >
                                          Select Variant
                                        </li>
                                        {Array.isArray(variants) && variants.map((v: any) => (
                                          <li
                                            key={v.id}
                                            onClick={() => {
                                              const next = [...revisedCharges];
                                              const targetCharge = { 
                                                ...next[idx], 
                                                productVariantId: v.id, 
                                                productVariant: v 
                                              };
                                              if (v.price) targetCharge.unitPrice = Number(v.price);
                                              if (v.name) targetCharge.description = v.name;
                                              next[idx] = targetCharge;
                                              setRevisedCharges(next);
                                              setOpenProductDropdownIndex(null);
                                            }}
                                            className={cn(
                                              "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                              charge.productVariantId === v.id && "bg-primary/5 text-primary font-bold"
                                            )}
                                          >
                                            {v.name}
                                          </li>
                                        ))}
                                      </ul>,
                                      document.body
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 min-w-[200px]">
                                  <input
                                    type="text"
                                    value={charge.description}
                                    onChange={(e) => handleChargeChange(idx, "description", e.target.value)}
                                    placeholder="e.g. Shipping Fee, Customs"
                                    className={cn(
                                      "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                      descError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                    required
                                  />
                                  {descError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1">
                                      {descError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-32">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={charge.unitPrice}
                                    onChange={(e) => handleChargeChange(idx, "unitPrice", e.target.value)}
                                    onKeyDown={(e) => blockNegativeKeys(e, charge.unitPrice)}
                                    placeholder="0.00"
                                    className={cn(
                                      "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                      unitPriceError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                    required
                                  />
                                  {unitPriceError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1 text-center">
                                      {unitPriceError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-24">
                                  <input
                                    type="text"
                                    inputMode={isIntegerUnit(charge.unit) ? "numeric" : "decimal"}
                                    value={charge.quantity}
                                    onChange={(e) => handleChargeChange(idx, "quantity", e.target.value)}
                                    onKeyDown={(e) => blockNegativeKeys(e, charge.quantity, isIntegerUnit(charge.unit))}
                                    placeholder="0"
                                    className={cn(
                                      "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                      quantityError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                  />
                                  {quantityError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1 text-center">
                                      {quantityError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-28">
                                  <button
                                    type="button"
                                    data-dropdown-trigger={`unit-${idx}`}
                                    onClick={(e) => {
                                      if (isRowDisabled) return
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      setUnitDropdownPosition({
                                        top: rect.bottom,
                                        left: rect.left,
                                        width: rect.width,
                                      })
                                      setOpenUnitDropdownIndex(prev => prev === idx ? null : idx)
                                    }}
                                    className={cn(
                                      "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                      unitError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                  >
                                    <span className={cn("truncate", !charge.unit && "text-slate-400 font-normal")}>
                                      {charge.unit
                                        ? (QUANTITY_UNITS.find((u) => u.code === charge.unit)?.name || charge.unit)
                                        : "Select"}
                                    </span>
                                    <ChevronDown
                                      className={cn(
                                        "size-3.5 text-slate-400 transition-transform shrink-0 ml-1",
                                        openUnitDropdownIndex === idx && "rotate-180"
                                      )}
                                    />
                                  </button>

                                  {openUnitDropdownIndex === idx && unitDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                    <ul
                                      ref={unitDropdownMenuRef}
                                      style={{
                                        position: "fixed",
                                        top: `${unitDropdownPosition.top + 4}px`,
                                        left: `${unitDropdownPosition.left}px`,
                                        width: `${Math.max(unitDropdownPosition.width, 150)}px`,
                                      }}
                                      className="z-[99999] max-h-[160px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                    >
                                      <li
                                        onClick={() => {
                                          handleChargeChange(idx, "unit", "")
                                          setOpenUnitDropdownIndex(null)
                                        }}
                                        className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50"
                                      >
                                        Select Unit
                                      </li>
                                      {QUANTITY_UNITS.map((u) => (
                                        <li
                                          key={u.code}
                                          onClick={() => {
                                            handleChargeChange(idx, "unit", u.code)
                                            setOpenUnitDropdownIndex(null)
                                          }}
                                          className={cn(
                                            "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                            charge.unit === u.code && "bg-primary/5 text-primary font-bold"
                                          )}
                                          title={u.name}
                                        >
                                          {u.name}
                                        </li>
                                      ))}
                                    </ul>,
                                    document.body
                                  )}
                                  {unitError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1 text-center whitespace-normal">
                                      {unitError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-28">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={charge.discount}
                                    onChange={(e) => handleChargeChange(idx, "discount", e.target.value)}
                                    onKeyDown={(e) => blockNegativeKeys(e, charge.discount)}
                                    placeholder="0"
                                    className={cn(
                                      "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                      discountError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                  />
                                  {discountError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1 text-center">
                                      {discountError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-24">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={charge.tax}
                                    onChange={(e) => handleChargeChange(idx, "tax", e.target.value)}
                                    onKeyDown={(e) => blockNegativeKeys(e, charge.tax)}
                                    placeholder="0"
                                    className={cn(
                                      "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                      taxError
                                        ? "border-primary focus:border-primary ring-1 ring-primary/20"
                                        : "border-slate-200 focus:border-primary",
                                      isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                    title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                  />
                                  {taxError && !isRowDisabled && (
                                    <p className="text-[10px] text-primary font-semibold mt-1 text-center">
                                      {taxError}
                                    </p>
                                  )}
                                </td>
                                <td className="p-2 w-24">
                                  <button
                                    type="button"
                                    data-dropdown-trigger={`taxtype-${idx}`}
                                    onClick={(e) => {
                                      if (isRowDisabled) return
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      setTaxTypeDropdownPosition({
                                        top: rect.bottom,
                                        left: rect.left,
                                        width: rect.width
                                      })
                                      setOpenTaxTypeDropdownIndex(prev => prev === idx ? null : idx)
                                    }}
                                    className={cn(
                                      "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                      isRowDisabled ? "border-slate-200 opacity-50 cursor-not-allowed bg-slate-50" : "border-slate-200 focus:border-primary"
                                    )}
                                    disabled={reviseMutation.isPending || isRowDisabled}
                                  >
                                    <span className="truncate">
                                      {charge.taxType === "NONE" ? "None" : (charge.taxType || "GST")}
                                    </span>
                                    <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openTaxTypeDropdownIndex === idx && "rotate-180")} />
                                  </button>

                                  {openTaxTypeDropdownIndex === idx && taxTypeDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                    <ul 
                                      ref={taxTypeDropdownMenuRef}
                                      style={{
                                        position: 'fixed',
                                        top: `${taxTypeDropdownPosition.top + 4}px`,
                                        left: `${taxTypeDropdownPosition.left}px`,
                                        width: `${taxTypeDropdownPosition.width}px`,
                                      }}
                                      className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                    >
                                      {[
                                        { value: "GST", label: "GST" },
                                        { value: "VAT", label: "VAT" },
                                        { value: "NONE", label: "None" }
                                      ].map((option) => (
                                        <li
                                          key={option.value}
                                          onClick={() => {
                                            handleChargeChange(idx, "taxType", option.value)
                                            setOpenTaxTypeDropdownIndex(null)
                                          }}
                                          className={cn(
                                            "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                            charge.taxType === option.value && "bg-primary/5 text-primary font-bold"
                                          )}
                                        >
                                          {option.label}
                                        </li>
                                      ))}
                                    </ul>,
                                    document.body
                                  )}
                                </td>
                                <td className="p-2 text-right text-slate-900 font-bold font-mono">
                                  <div className="h-[34px] flex items-center justify-end">
                                    {total.toFixed(2)}
                                  </div>
                                </td>
                                <td className="p-2 text-center">
                                  <div className="h-[34px] flex items-center justify-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCharge(idx)}
                                      disabled={reviseMutation.isPending || revisedCharges.length <= 1}
                                      className="p-1.5 rounded-md border border-destructive/20 bg-destructive/10 hover:bg-destructive/20 text-destructive transition-all cursor-pointer shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                      title="Delete Item"
                                    >
                                      <Trash2 className="size-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-primary font-mono">
                      {revisedCurrency} {revisedCharges.reduce((acc, charge) => {
                        const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                        const base = (Number(charge.unitPrice) || 0) * qty
                        const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                        return acc + Math.max(0, subtotal + (subtotal * ((Number(charge.tax) || 0) / 100)))
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {reviseMutation.isError && (
                  <div className="text-xs font-semibold text-primary bg-primary/10 border border-primary/20 p-2.5 rounded-lg">
                    {reviseMutation.error.message || "Failed to revise quotation."}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsRevising(false)}
                    className="border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold text-xs px-4 py-2 rounded-lg transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={reviseMutation.isPending}
                    className="bg-primary hover:bg-primary-hover text-primary-foreground font-bold text-xs px-4 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Submit Revision
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <FileText className="size-3.5 text-primary" /> Quotation Message
                  </h4>
                  <p className="text-sm text-slate-700 font-medium bg-primary/[0.03] p-3.5 rounded-xl border border-primary/15 hover:border-primary/25 transition-colors leading-relaxed italic">
                    {quotation.message || "No message provided"}
                  </p>
                </div>

                {(quotation.commodity || quotation.totalPieces || quotation.weight || quotation.volumeWeight) && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Package className="size-3.5 text-primary" /> Logistics & Shipment Details
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 bg-slate-50/50 border border-slate-200/60 rounded-xl p-4 text-xs font-semibold text-slate-700">
                      {quotation.commodity && (
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Commodity</span>
                          <span className="text-slate-800">{quotation.commodity}</span>
                        </div>
                      )}
                      {quotation.totalPieces && (
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Total Pieces</span>
                          <span className="text-slate-800">{quotation.totalPieces}</span>
                        </div>
                      )}
                      {quotation.weight && (
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Weight (kg)</span>
                          <span className="text-slate-800">{quotation.weight}</span>
                        </div>
                      )}
                      {quotation.volumeWeight && (
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Volume Weight (kg)</span>
                          <span className="text-slate-800">{quotation.volumeWeight}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Package className="size-3.5 text-primary" /> Charges Breakdown
                    </h4>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3">
                    {charges.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                          <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                            <tr>
                              <th className="p-3 min-w-[160px]">Description</th>
                              <th className="p-3 text-center w-32">Unit Price</th>
                              <th className="p-3 text-center w-24">Quantity</th>
                              <th className="p-3 text-center w-24">Unit</th>
                              <th className="p-3 text-center w-28">Discount (%)</th>
                              <th className="p-3 text-center w-24">Tax (%)</th>
                              <th className="p-3 text-center w-24">Tax Type</th>
                              <th className="p-3 text-right">Total</th>
                              <th className="p-3 w-20"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 bg-white">
                            {charges.map((charge: any, idx: number) => {
                              const isAdvanced = charge.unitPrice !== undefined;
                              
                              let lineTotal = charge.amount || 0;
                              if (isAdvanced) {
                                const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1;
                                const base = Number(charge.unitPrice) * qty;
                                const subtotal = base - (base * (Number(charge.discount || 0) / 100));
                                lineTotal = Math.max(0, subtotal + (subtotal * (Number(charge.tax || 0) / 100)));
                              }

                              return (
                                <tr key={charge.id || idx} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="p-3 text-slate-800 font-semibold">
                                    {(charge.productVariantId || charge.productVariant) ? (
                                       <button
                                         type="button"
                                         onClick={() => {
                                           const vId = charge.productVariantId || charge.productVariant?.id
                                           if (vId) {
                                             setSelectedDetailVariantId(vId)
                                           } else if (charge.productVariant) {
                                             setSelectedDetailVariant(charge.productVariant)
                                           }
                                         }}
                                         className="text-primary hover:text-primary-hover hover:underline text-left cursor-pointer transition-colors"
                                       >
                                         {charge.description}
                                       </button>
                                     ) : (
                                       <span className="text-slate-800">{charge.description}</span>
                                     )}
                                  </td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? charge.unitPrice : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.measureUnit || charge.unit || "-") : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                                  <td className="p-3 text-center text-slate-600">
                                    {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                                  </td>
                                  <td className="p-3 text-right text-slate-900 font-bold font-mono">
                                     {lineTotal.toFixed(2)}
                                  </td>
                                  <td className="p-3"></td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-primary font-mono">
                      {quotation.currencyCode} {(typeof totalAmount === 'number' ? totalAmount : Number(totalAmount || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )}

          </div>

          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-primary/10 lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-primary" /> Timestamps
              </h4>

              <div className="relative pl-5 space-y-4">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-primary/15"></div>

                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-sky-100">
                    <div className="size-1.5 rounded-full bg-sky-600"></div>
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">SENT DATE</span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(quotation.createdAt)}</p>
                  </div>
                </div>

                {(quotation.expiresAt || quotation.validUntil) && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-amber-100 bg-amber-100">
                      <div className="size-1.5 rounded-full bg-amber-600"></div>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">VALID UNTIL</span>
                      <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(quotation.expiresAt || quotation.validUntil)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Empty space on mobile only when revise quotation form is open so user can scroll above keyboard */}
      {isRevising && (
        <div className="h-16 sm:h-8 md:hidden pointer-events-none" aria-hidden="true" />
      )}

      {(selectedDetailVariant || selectedDetailVariantId) && (
        <VariantDetailSidebar
          variant={selectedDetailVariant || undefined}
          variantId={selectedDetailVariantId || undefined}
          onClose={() => {
            setSelectedDetailVariant(null)
            setSelectedDetailVariantId(null)
          }}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={false}
        />
      )}
      {selectedProduct && (
        <ProductDetailSidebar
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
          showCompanySpec={false}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar 
          company={selectedCompany} 
          onClose={() => setSelectedCompany(null)} 
          level={selectedProduct || selectedDetailVariant ? 1 : 0}
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar 
          externalSeller={selectedExternalSeller} 
          onClose={() => setSelectedExternalSeller(null)} 
        />
      )}
      {selectedRfq && (
        <RfqDetailSidebar 
          rfq={selectedRfq} 
          onClose={() => setSelectedRfq(null)} 
          isSeller={true}
        />
      )}
    </div>
  )
}
